import { Component, Injector, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseModalComponent } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { AccountType, FinancialAccount, PostingModule, SubledgerType } from '../../../../models/financial-account/financial-account';
import { LocaleHelperService } from '../../../../services/locale-helper.service';
import { CURRENCY_OPTIONS, Currency, currencyFromApi, currencyLabel } from '../../../../utilities/currency.util';
import { postingModuleOptions as buildPostingModuleOptions } from '../../../../utilities/posting-module.util';
import { nextChildAccountNumber } from '../../../../utilities/account-number.util';

interface Option<T> {
  label: string;
  value: T;
}

interface AccountCreateForm {
  parentId: FormControl<string | null>;
  accountNumber: FormControl<string>;
  nameEn: FormControl<string>;
  nameEs: FormControl<string>;
  accountType: FormControl<AccountType>;
  currency: FormControl<Currency>;
  subledgerType: FormControl<SubledgerType | null>;
  postingModules: FormControl<PostingModule[]>;
}

/**
 * Creates a financial (GL) account as a child under a chosen parent — the new account is a posting (leaf)
 * account; the server derives its Level / NormalBalance and flips the parent to a group account. Optionally
 * marks it as a subledger control account, in which case at least one authorized posting module is required.
 *
 * <p>Number and account type are taken from the parent, not asked for: the number encodes the hierarchy,
 * and NormalBalance is derived from the type server-side while the tree rolls balances up blindly — a
 * Revenue child under an Asset parent would corrupt the parent's total. Currency IS asked for, because a
 * child does not inherit it in practice (a USD bank account sits happily under an HNL "Banks" parent).</p>
 */
@Component({
  selector: 'app-account-create-modal',
  templateUrl: './account-create-modal.component.html',
  styleUrl: './account-create-modal.component.scss',
})
export class AccountCreateModalComponent extends BaseModalComponent<FinancialAccount | null> implements OnInit {
  /** Pre-selected parent (from the "+" on a tree row). When set, the parent field is locked. */
  @Input() parent: FinancialAccount | null = null;
  /** All company accounts — the parent picker options. */
  @Input() accounts: FinancialAccount[] = [];
  @Input() companyReference = '';
  /** The owning company's currency — the default for a new account (overridden by a chosen parent). */
  @Input() defaultCurrency: Currency = Currency.HNL;

  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LocaleHelperService);

  readonly form: FormGroup<AccountCreateForm> = new FormGroup<AccountCreateForm>({
    parentId: new FormControl<string | null>(null, { validators: [Validators.required] }),
    accountNumber: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    nameEn: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    nameEs: new FormControl<string>('', { nonNullable: true }),
    accountType: new FormControl<AccountType>(AccountType.Asset, { nonNullable: true, validators: [Validators.required] }),
    currency: new FormControl<Currency>(Currency.HNL, { nonNullable: true, validators: [Validators.required] }),
    subledgerType: new FormControl<SubledgerType | null>(null),
    postingModules: new FormControl<PostingModule[]>([], { nonNullable: true }),
  });

  /** A subledger account must authorize at least one posting module (controlled-account invariant). */
  readonly isSubledger = signal(false);

  /** Resolved parent, once chosen — drives the read-only number and type boxes. */
  readonly selectedParent = signal<FinancialAccount | null>(null);
  /**
   * True when no number could be derived — the parent's number leaves no digits for another level. The
   * field stays read-only either way; a number is never typed by hand, so this blocks the save instead.
   */
  readonly numberUnavailable = signal(false);

  readonly parentTypeLabel = computed(() => {
    const type = this.selectedParent()?.accountType;
    return this.accountTypeOptions.find((o) => o.value === type)?.label ?? '';
  });

  readonly parentOptions = computed<Option<string>[]>(() =>
    this.accounts.filter((a) => a.id).map((a) => ({ label: `${a.accountNumber} · ${this.accountLabel(a)}`, value: a.id! })),
  );

  readonly currencyOptions = CURRENCY_OPTIONS;

  readonly accountTypeOptions: Option<AccountType>[] = [
    { label: this.t('COA_Type_Asset', 'Asset'), value: AccountType.Asset },
    { label: this.t('COA_Type_Liability', 'Liability'), value: AccountType.Liability },
    { label: this.t('COA_Type_Equity', 'Equity'), value: AccountType.Equity },
    { label: this.t('COA_Type_Revenue', 'Revenue'), value: AccountType.Revenue },
    { label: this.t('COA_Type_Expense', 'Expense'), value: AccountType.Expense },
  ];

  readonly subledgerOptions: Option<SubledgerType | null>[] = [
    { label: this.t('COA_Subledger_None', 'None (ordinary account)'), value: null },
    { label: this.t('COA_Subledger_Provider', 'Provider'), value: SubledgerType.Provider },
    { label: this.t('COA_Subledger_Client', 'Client'), value: SubledgerType.Client },
    { label: this.t('COA_Subledger_Bank', 'Bank'), value: SubledgerType.Bank },
    { label: this.t('COA_Subledger_InternalCompany', 'Internal company'), value: SubledgerType.InternalCompany },
    { label: this.t('COA_Subledger_Employee', 'Employee'), value: SubledgerType.Employee },
    { label: this.t('COA_Subledger_PettyCashFund', 'Petty cash fund'), value: SubledgerType.PettyCashFund },
  ];

  readonly postingModuleOptions: Option<PostingModule>[] = buildPostingModuleOptions((k, f) => this.t(k, f));

  constructor(injector: Injector) {
    super(injector);

    this.form.controls.subledgerType.valueChanges.subscribe((type) => this.onSubledgerTypeChange(type));
    this.form.controls.parentId.valueChanges.subscribe((id) => this.applyParentDefaults(id));
  }

  ngOnInit(): void {
    this.form.controls.currency.setValue(this.defaultCurrency);
    if (this.parent?.id) {
      this.form.controls.parentId.setValue(this.parent.id);
      this.form.controls.parentId.disable();
      this.applyParentDefaults(this.parent.id);
    }
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private accountLabel(account: FinancialAccount): string {
    return this.locale.getLocalizedValue(account.accountName) || account.accountNumber;
  }

  /**
   * A child takes its type and number from the parent, and defaults (only) its currency to the parent's.
   */
  private applyParentDefaults(parentId: string | null): void {
    const parent = this.accounts.find((a) => a.id === parentId) ?? null;
    this.selectedParent.set(parent);
    if (!parent) return;

    this.form.controls.accountType.setValue(parent.accountType);

    // Reads come back as the Currency enum number, not the "HNL" code the entity stores.
    const parentCurrency = currencyFromApi(parent.currency);
    if (parentCurrency != null) this.form.controls.currency.setValue(parentCurrency);

    const derived = nextChildAccountNumber(parent, this.accounts);
    this.numberUnavailable.set(derived === null);
    // Kept enabled but readonly, so Validators.required still fires when nothing could be derived —
    // a disabled control is skipped by validation and would let an empty number through.
    this.form.controls.accountNumber.setValue(derived ?? '');
  }

  private onSubledgerTypeChange(type: SubledgerType | null): void {
    const subledger = type != null;
    this.isSubledger.set(subledger);
    const modules = this.form.controls.postingModules;
    if (subledger) {
      modules.addValidators(Validators.required);
    } else {
      modules.removeValidators(Validators.required);
    }
    modules.updateValueAndValidity();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const subledgerType = v.subledgerType ?? null;
    // An ordinary account may authorize modules too — that is how a non-subledger account is reserved to
    // (or opened up for) a module. The server invariant runs one way only: subledger => at least one module.
    const account: FinancialAccount = {
      accountNumber: v.accountNumber.trim(),
      accountName: { en: v.nameEn.trim(), es: (v.nameEs || v.nameEn).trim() },
      accountType: v.accountType,
      financialCompanyReference: this.companyReference,
      parentAccountId: v.parentId,
      isPostingAccount: true,
      isBlocked: false,
      // Stored as the currency code (e.g. "HNL") to stay compatible with the string field on the account.
      currency: currencyLabel(v.currency),
      subledgerType,
      authorizedPostingModules: v.postingModules ?? [],
    };
    this.close({ action: 'save', data: account });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }
}
